import {
  Smartphone,
  Layers,
  PenTool,
  Server,
  Globe,
  Wrench,
  ShieldCheck,
  Wallet,
  RefreshCw,
  Sparkles,
  BrainCircuit,
  Headphones,
  Megaphone,
  Building2,
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Leistungen", href: "#leistungen" },
  { label: "KI-Agenten", href: "/ki-agenten", route: true },
  { label: "Versprechen", href: "#versprechen" },
  { label: "Ablauf", href: "#ablauf" },
  { label: "\u00dcber uns", href: "#ueber-uns" },
  { label: "Projekte", href: "#projekte" },
  { label: "Kontakt", href: "#kontakt" },
];

// The flagship specialty — rendered as a highlighted hero card. Links to /ki-agenten.
export const SPECIALTY = {
  icon: BrainCircuit,
  badge: "Unsere Spezialit\u00e4t",
  title: "KI-Agenten & Automatisierung",
  to: "/ki-agenten",
  desc: "Wir bauen autonome KI-Agenten, die mitdenken, entscheiden und handeln, von einzelnen Assistenten bis zu vernetzten Multi-Agenten-Systemen wie unserem eigenen Liberox Genesis. Statt isolierter Chatbots entsteht eine digitale Organisation aus spezialisierten Einheiten, die auf gemeinsamem Unternehmenswissen aufbaut.",
  points: [
    "Spezialisierte Agenten f\u00fcr Sales, Support, Marketing & Backoffice",
    "Multi-Agenten-Systeme mit gemeinsamem Wissen & Entscheidungslogik (MAGI)",
    "Anbindung an CRM, E-Mail, Microsoft 365 & interne Systeme",
    "Konkrete Aktionen statt nur Text, vom Erkennen bis zur Ausf\u00fchrung",
    "Menschliche Freigaben, Notstopp & volle Nachvollziehbarkeit",
    "RAG, LLM-Integration (OpenAI, Anthropic, Google) & massgeschneiderte Tools",
  ],
};

export const SERVICES = [
  {
    slug: "web-apps-plattformen",
    icon: Globe,
    title: "Web-Apps & Plattformen",
    desc: "Skalierbare Web-Applikationen f\u00fcr echte Gesch\u00e4ftsprozesse, vom SaaS-Dashboard bis zur mehrsprachigen Plattform.",
    metaTitle: "Web-App & Plattform Entwicklung Schweiz",
    metaDescription: "Massgeschneiderte Web-Apps & Plattformen aus der Schweiz: SaaS-Dashboards, Kundenportale, PWAs und mehrsprachige Portale, performant, sicher und skalierbar.",
    intro: "Wir entwickeln performante, skalierbare Web-Applikationen, die echte Gesch\u00e4ftsprozesse abbilden, vom internen Tool bis zur \u00f6ffentlichen Plattform mit hunderttausenden Aufrufen.",
    long: [
      "Ob SaaS-Dashboard, Kundenportal, Buchungssystem oder mehrsprachige Plattform: Wir bauen Web-Apps rund um deine tats\u00e4chlichen Anforderungen statt um eine starre Standardsoftware. Mit React im Frontend und einer sauberen API-Architektur entstehen Produkte, die sich schnell anf\u00fchlen und mit deinem Unternehmen mitwachsen.",
      "Von der Informationsarchitektur \u00fcber Rollen- und Berechtigungskonzepte bis zu Echtzeit-Funktionen und Offline-F\u00e4higkeit als Progressive Web App: Wir denken Performance, SEO und Wartbarkeit von Anfang an mit.",
    ],
    points: [
      "SaaS-Dashboards, Kundenportale & interne Tools",
      "Progressive Web Apps (PWA) & Echtzeit-Funktionen",
      "Mehrsprachigkeit, Rollen & Berechtigungen",
    ],
    benefits: [
      "Kurze Ladezeiten & Top Core Web Vitals",
      "Skalierbare Architektur f\u00fcr wachsende Nutzerzahlen",
      "Sauberes technisches SEO f\u00fcr organische Sichtbarkeit",
    ],
    tech: ["React", "FastAPI", "MongoDB", "PWA", "Kubernetes"],
    faqs: [
      { q: "Was kostet eine individuelle Web-App?", a: "Wir arbeiten mit transparenten Fixpreisen. Nach einem kurzen Kickoff erh\u00e4ltst du ein klares Angebot ohne versteckte Kosten, inklusive 100% Geld-zur\u00fcck-Garantie." },
      { q: "K\u00f6nnt ihr bestehende Systeme anbinden?", a: "Ja. Wir integrieren CRM, ERP, Microsoft 365, Zahlungs- und Registerdaten sowie beliebige Drittanbieter-APIs." },
      { q: "Ist die Web-App mehrsprachig?", a: "Auf Wunsch bauen wir mehrsprachige, vorgerenderte Inhalte f\u00fcr maximale SEO-Sichtbarkeit, z. B. Deutsch, Franz\u00f6sisch, Italienisch und Englisch." },
    ],
    featured: true,
  },
  {
    slug: "mobile-apps-ios-android",
    icon: Smartphone,
    title: "iOS & Android (Native)",
    desc: "Native Apps mit kompromissloser Performance, fl\u00fcssigen Animationen und tiefer Systemintegration.",
    metaTitle: "Native App-Entwicklung f\u00fcr iOS & Android",
    metaDescription: "Native iOS- & Android-Apps aus der Schweiz: kompromisslose Performance, fl\u00fcssige Animationen, Push, Offline-Sync und App-Store-Deployment.",
    intro: "Wir entwickeln native Mobile-Apps, die sich schnell, fl\u00fcssig und hochwertig anf\u00fchlen, mit tiefer Integration in die M\u00f6glichkeiten von iPhone und Android.",
    long: [
      "Wenn maximale Performance und ein erstklassiges Nutzererlebnis z\u00e4hlen, setzen wir auf native Entwicklung mit Swift und Kotlin, oder auf React Native, wenn eine gemeinsame Codebasis Sinn ergibt. So nutzen wir Kamera, Standort, Push-Benachrichtigungen, Offline-Sync und weitere Ger\u00e4te-APIs optimal aus.",
      "Wir begleiten den gesamten Weg: von Konzept und Design \u00fcber Entwicklung und Tests bis zur Ver\u00f6ffentlichung im Apple App Store und Google Play Store, inklusive laufender Updates.",
    ],
    points: [
      "Swift, Kotlin & React Native",
      "Push, Offline-Sync & Ger\u00e4te-APIs",
      "App-Store- & Play-Store-Deployment",
    ],
    benefits: [
      "Kompromisslose Performance & fl\u00fcssige Animationen",
      "Tiefe Systemintegration (Kamera, Standort, Push)",
      "Betreuung inkl. Store-Deployment & Updates",
    ],
    tech: ["Swift", "Kotlin", "React Native"],
    faqs: [
      { q: "Native oder Cross-Platform: Was ist besser?", a: "Das h\u00e4ngt von deinen Anforderungen ab. Bei h\u00f6chsten Performance-Anspr\u00fcchen empfehlen wir nativ; f\u00fcr schnellere Time-to-Market und geteilte Codebasis oft Cross-Platform. Wir beraten dich ehrlich." },
      { q: "\u00dcbernehmt ihr das App-Store-Deployment?", a: "Ja, wir k\u00fcmmern uns um Zertifikate, Store-Listings und die Ver\u00f6ffentlichung in beiden Stores." },
    ],
  },
  {
    slug: "cross-platform-apps",
    icon: Layers,
    title: "Cross-Platform",
    desc: "Ein Code, mehrere Plattformen. React & React Native f\u00fcr schnellere Time-to-Market ohne Qualit\u00e4tsverlust.",
    metaTitle: "Cross-Platform App-Entwicklung (React Native)",
    metaDescription: "Cross-Platform-Apps mit React & React Native: eine Codebasis f\u00fcr iOS, Android und Web, schneller am Markt, geringere Kosten, konsistente UX.",
    intro: "Eine gemeinsame Codebasis f\u00fcr iOS, Android und Web, f\u00fcr schnellere Time-to-Market und geringere Kosten, ohne bei der User Experience Kompromisse zu machen.",
    long: [
      "Mit React und React Native entwickeln wir Apps, die auf mehreren Plattformen laufen und trotzdem nativ wirken. Das spart Zeit und Budget, weil Logik und grosse Teile der Oberfl\u00e4che nur einmal gebaut werden.",
      "Wir achten dabei konsequent auf konsistente UX, Plattform-spezifische Feinheiten und Wartbarkeit, damit dein Produkt auf jedem Ger\u00e4t \u00fcberzeugt.",
    ],
    points: [
      "Gemeinsame Codebasis f\u00fcr iOS, Android & Web",
      "Konsistente UX \u00fcber alle Ger\u00e4te",
      "Schnellere Entwicklung, geringere Kosten",
    ],
    benefits: [
      "Deutlich schnellere Time-to-Market",
      "Geringere Entwicklungs- & Wartungskosten",
      "Ein Team, ein Stack, konsistente Qualit\u00e4t",
    ],
    tech: ["React", "React Native", "Expo"],
    faqs: [
      { q: "Leidet die Performance bei Cross-Platform?", a: "Bei sauberer Umsetzung sind moderne Cross-Platform-Apps f\u00fcr die allermeisten Anwendungsf\u00e4lle exzellent performant. Nur bei Spezialf\u00e4llen empfehlen wir nativ." },
    ],
  },
  {
    slug: "ui-ux-design",
    icon: PenTool,
    title: "UI/UX Design",
    desc: "Produkt-Design, das begeistert, von der ersten Skizze bis zum pixelgenauen Interface im Premium-Look.",
    metaTitle: "UI/UX Design & Produkt-Design Schweiz",
    metaDescription: "Premium UI/UX Design aus der Schweiz: User Research, Wireframes, interaktive Prototypen und Design-Systeme, inkl. Glassmorphism, Dark/Light & Barrierefreiheit.",
    intro: "Wir gestalten digitale Produkte, die begeistern und intuitiv bedienbar sind, von der ersten Skizze \u00fcber interaktive Prototypen bis zum pixelgenauen Interface.",
    long: [
      "Gutes Design ist mehr als Optik: Es macht Produkte verst\u00e4ndlich, schnell und angenehm. Wir starten mit User Research, Flows und Wireframes, bevor wir ein hochwertiges, konsistentes Interface entwerfen, auf Wunsch mit modernem Glassmorphism-Look.",
      "Wir liefern durchg\u00e4ngige Design-Systeme, die Entwicklung beschleunigen und Konsistenz sichern, inklusive Dark-/Light-Mode und Barrierefreiheit.",
    ],
    points: [
      "User Research, Flows & Wireframes",
      "Interaktive Prototypen & Design-Systeme",
      "Glassmorphism, Dark/Light & Barrierefreiheit",
    ],
    benefits: [
      "H\u00f6here Conversion & Nutzerzufriedenheit",
      "Konsistente Design-Systeme f\u00fcr schnellere Umsetzung",
      "Barrierefreie, moderne Interfaces",
    ],
    tech: ["Figma", "Design Systems", "Prototyping"],
    faqs: [
      { q: "Bietet ihr Design auch ohne Entwicklung an?", a: "Ja. Wir \u00fcbernehmen reines Produkt-Design ebenso wie Design plus vollst\u00e4ndige Umsetzung, ganz wie du es brauchst." },
    ],
  },
  {
    slug: "backend-apis-cloud",
    icon: Server,
    title: "Backend, APIs & Cloud",
    desc: "Sichere, skalierbare Architektur und Cloud-Infrastruktur, die mit deinem Produkt mitw\u00e4chst.",
    metaTitle: "Backend-, API- & Cloud-Entwicklung",
    metaDescription: "Skalierbare Backends, sichere APIs und Cloud-Infrastruktur: FastAPI, Realtime-APIs, Microsoft Entra ID, Docker & Kubernetes, CI/CD und Objektspeicher.",
    intro: "Wir bauen das robuste Fundament deines Produkts: sichere APIs, skalierbare Architektur und Cloud-Infrastruktur, die zuverl\u00e4ssig mitw\u00e4chst.",
    long: [
      "Ein starkes Backend entscheidet \u00fcber Performance, Sicherheit und Skalierbarkeit. Wir setzen auf moderne Frameworks wie FastAPI, dokumentenorientierte Datenbanken sowie REST- und Realtime-APIs, sauber strukturiert und dokumentiert.",
      "Authentifizierung \u00fcber Microsoft Entra ID, getrennte Objektspeicher f\u00fcr Dokumente, Containerisierung mit Docker und Betrieb auf Kubernetes sowie automatisierte CI/CD-Pipelines: So bleibt dein System sicher, wartbar und ausfallsicher.",
    ],
    points: [
      "FastAPI, Node & dokumentenorientierte Datenbanken",
      "REST- & Realtime-APIs, Auth (Microsoft Entra ID)",
      "Docker & Kubernetes, CI/CD & Objektspeicher",
    ],
    benefits: [
      "Skalierbar & ausfallsicher unter Last",
      "Sichere Authentifizierung & Datentrennung",
      "Automatisierte Deployments via CI/CD",
    ],
    tech: ["FastAPI", "Node", "Docker", "Kubernetes", "Microsoft Entra ID"],
    faqs: [
      { q: "K\u00f6nnt ihr ein bestehendes Backend \u00fcbernehmen?", a: "Ja, wir \u00fcbernehmen, refactoren oder erweitern bestehende Systeme und richten bei Bedarf sauberes Monitoring und CI/CD ein." },
    ],
  },
  {
    slug: "wartung-support",
    icon: Wrench,
    title: "Wartung & Support",
    desc: "Nach dem Launch ist vor dem Update: wir halten dein Produkt aktuell, sicher und leistungsstark.",
    metaTitle: "App-Wartung, Support & Weiterentwicklung",
    metaDescription: "Zuverl\u00e4ssige Wartung & Support f\u00fcr Apps und Plattformen: Monitoring, Bugfixes, Security-Updates, neue Features und klare Reaktionszeiten.",
    intro: "Wir lassen dich nach dem Launch nicht allein: Mit Monitoring, Updates und laufender Weiterentwicklung bleibt dein Produkt aktuell, sicher und leistungsstark.",
    long: [
      "Software lebt. Wir sorgen daf\u00fcr, dass deine App zuverl\u00e4ssig l\u00e4uft: proaktives Monitoring, schnelle Bugfixes, regelm\u00e4ssige Security-Updates und Kompatibilit\u00e4t mit neuen Betriebssystem-Versionen.",
      "Dar\u00fcber hinaus entwickeln wir dein Produkt kontinuierlich weiter, mit neuen Features, Optimierungen und verl\u00e4sslichem Support mit klaren Reaktionszeiten.",
    ],
    points: [
      "Monitoring, Bugfixes & Security-Updates",
      "Neue Features & laufende Weiterentwicklung",
      "Verl\u00e4sslicher Support mit klaren Reaktionszeiten",
    ],
    benefits: [
      "Weniger Ausf\u00e4lle dank proaktivem Monitoring",
      "Immer sicher & aktuell",
      "Planbare Weiterentwicklung",
    ],
    tech: ["Monitoring", "CI/CD", "Security"],
    faqs: [
      { q: "Betreut ihr auch Apps, die ihr nicht gebaut habt?", a: "Ja. Nach einem kurzen Code-Review \u00fcbernehmen wir gern Wartung und Weiterentwicklung bestehender Produkte." },
    ],
  },
];

export const PROMISES = [
  {
    icon: ShieldCheck,
    title: "100% Geld-zur\u00fcck-Garantie",
    desc: "Bist du mit dem Ergebnis nicht zufrieden, bekommst du dein Geld vollst\u00e4ndig zur\u00fcck, ohne Wenn und Aber.",
    featured: true,
  },
  {
    icon: Wallet,
    title: "Transparente Fixpreise",
    desc: "Klare Kosten von Anfang an. Keine versteckten Geb\u00fchren, keine b\u00f6sen \u00dcberraschungen.",
  },
  {
    icon: RefreshCw,
    title: "Laufende Updates & Wartung",
    desc: "Wir lassen dich nach dem Launch nicht allein: regelm\u00e4\u00dfige Updates, neue Features und Support.",
  },
  {
    icon: Sparkles,
    title: "Apps & Web-Plattformen",
    desc: "Wir entwickeln dein Produkt von Grund auf neu, ob Mobile-App, Web-App oder Plattform, individuell auf deine Idee zugeschnitten.",
  },
];

export const STATS = [
  { label: "Geld-zur\u00fcck-Garantie", value: 100, suffix: "%" },
  { label: "Transparente Fixpreise", value: 100, suffix: "%" },
  { label: "\u00d8 Time-to-MVP", value: 10, suffix: " Wochen" },
  { label: "Gegr\u00fcndet", value: 2022, suffix: "", raw: true },
];

export const PROCESS = [
  {
    step: "01",
    title: "Kickoff & Ziele",
    points: ["Vision & Scope definieren", "Transparentes Fixpreis-Angebot"],
  },
  {
    step: "02",
    title: "UX & Prototyping",
    points: ["User Flows & Wireframes", "Interaktiver Prototyp"],
  },
  {
    step: "03",
    title: "Build Sprint",
    points: ["W\u00f6chentliche Releases", "Transparente Roadmap"],
  },
  {
    step: "04",
    title: "QA & Launch",
    points: ["Automatisierte Tests", "App-Store Deployment"],
  },
  {
    step: "05",
    title: "Updates & Wartung",
    points: ["Laufende Updates", "Support nach dem Launch"],
  },
];


// Steps of an autonomous agent cycle (rendered as a stepper on /ki-agenten).
export const AI_FLOW = [
  { title: "Erkennen", desc: "Relevante Unternehmen, Signale und Anfragen werden automatisiert identifiziert." },
  { title: "Bewerten", desc: "Das MAGI-Entscheidungssystem pr\u00fcft mehrstufig und aus verschiedenen Blickwinkeln." },
  { title: "Ansprechen", desc: "Personalisierte Erstkontakte und Inhalte werden erstellt und ausgel\u00f6st." },
  { title: "Nachfassen", desc: "Automatische, konsequente Follow-ups. Nichts geht mehr verloren." },
  { title: "\u00dcbergeben", desc: "Qualifizierte Vorg\u00e4nge werden sauber an den Vertrieb \u00fcbergeben." },
];

// Featured + teaser use cases for the KI-Agenten landing page.
export const AI_USE_CASES = [
  {
    id: "vertrieb-neukundengewinnung",
    icon: BrainCircuit,
    category: "Vertrieb & Sales",
    title: "Autonome Neukunden-Gewinnung im Vertrieb",
    summary:
      "KI-Agenten identifizieren, bewerten und kontaktieren potenzielle Kunden eigenst\u00e4ndig. Das MAGI-Entscheidungssystem sorgt f\u00fcr transparente, begr\u00fcndete Entscheidungen. Der Mensch beh\u00e4lt per Freigabe und Notstopp jederzeit die Kontrolle.",
    situation:
      "Qualifizierte Leads zu finden, richtig einzusch\u00e4tzen und konsequent nachzufassen, bindet im Vertrieb enorm viel Zeit, meist verteilt auf mehrere Tools und manuelle Schritte. Genau hier setzt Liberox Genesis an.",
    solution:
      "Ein Team spezialisierter KI-Agenten \u00fcbernimmt den gesamten Vertriebszyklus, von der Identifikation potenzieller Firmen \u00fcber die Bewertung und Priorisierung bis hin zu personalisierten Erstkontakt-E-Mails und automatischen Follow-ups. Jeder Agent ist auf eine klare Aufgabe fokussiert und arbeitet Hand in Hand mit den anderen.",
    magi:
      "\u00dcber allem steht das MAGI-Entscheidungssystem: Es bewertet jeden Lead mehrstufig und aus verschiedenen Blickwinkeln, bevor gehandelt wird, transparent begr\u00fcndet und jederzeit nachvollziehbar. Das lernende Superhirn speist relevantes Kontextwissen ein und wertet jede Kampagnen-Woche aus, um den Prozess kontinuierlich zu sch\u00e4rfen.",
    control:
      "Der Zyklus l\u00e4uft eigenst\u00e4ndig und rund um die Uhr. Der Mensch bleibt dabei jederzeit in Kontrolle. Wichtige Schritte k\u00f6nnen freigegeben werden, jede Entscheidung ist einsehbar, und ein zentraler Notstopp h\u00e4lt auf Knopfdruck sofort alle Prozesse an.",
    results: [
      "Deutlich weniger manueller Aufwand bei h\u00f6herer Konsequenz im Nachfassen",
      "Nachvollziehbare, begr\u00fcndete Entscheidungen statt \u201eBlack-Box\u201c-Automatik",
      "Volle Kosten- und Auslastungstransparenz in Echtzeit",
      "Ein System, das mit jeder Woche pr\u00e4ziser und wirkungsvoller wird",
    ],
    reference: { name: "Liberox Genesis", to: "/projekte/liberox-genesis" },
    featured: true,
  },
  {
    id: "support-triage",
    icon: Headphones,
    category: "Support",
    title: "Automatisierte Anfrage-Triage im Support",
    summary:
      "Agenten analysieren eingehende Anfragen, priorisieren nach Dringlichkeit, bereiten Antworten vor und unterst\u00fctzen das Team bei wiederkehrenden Support-Prozessen.",
  },
  {
    id: "marketing-kampagnen",
    icon: Megaphone,
    category: "Marketing",
    title: "Kampagnen von Analyse bis Ver\u00f6ffentlichung",
    summary:
      "Agenten analysieren relevante Informationen, planen Kampagnen, erstellen Inhalte und koordinieren die Ver\u00f6ffentlichung, abgestimmt auf Zielgruppe und Kanal.",
  },
  {
    id: "backoffice-kundenprozesse",
    icon: Building2,
    category: "Kunden & Backoffice",
    title: "Wiederkehrende Kundenprozesse automatisieren",
    summary:
      "Agenten bearbeiten kundenbezogene Abl\u00e4ufe, pflegen Informationen und \u00fcbernehmen wiederkehrende administrative Aufgaben, zuverl\u00e4ssig und nachvollziehbar.",
  },
];
